import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import ThemedText from '../components/common/ThemedText';
import { storage } from '../utils/storage';
import * as adminApi from '../lib/api/admin';
import { Alert } from '../utils/toast';

interface User {
  id: string;
  name: string;
  email?: string;
  phone: string;
  photo?: string;
  businessName?: string;
  profession?: string;
  city?: string;
}

const AdminNotificationScreen: React.FC = () => {
  const navigation = useNavigation();
  const { theme } = useTheme();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);



  // Send notification
  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      Alert.alert('Error', 'Please enter both title and message');
      return;
    }

    Alert.alert(
      'Confirm Send',
      'Send notification to all users?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Send',
          onPress: async () => {
            setIsSending(true);
            try {
              const token = await storage.getToken();
              if (!token) {
                Alert.alert('Error', 'Please login first');
                setIsSending(false);
                return;
              }

              const response = await adminApi.sendNotification(token, {
                title: title.trim(),
                message: message.trim(),
              });

              if (response.success) {
                Alert.alert(
                  'Success',
                  response.message,
                  [
                    {
                      text: 'OK',
                      onPress: () => {
                        setTitle('');
                        setMessage('');
                      },
                    },
                  ]
                );
              }
            } catch (error: any) {
              console.error('Error sending notification:', error);
              Alert.alert(
                'Error',
                error.message || 'Failed to send notification'
              );
            } finally {
              setIsSending(false);
            }
          },
        },
      ]
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={styles.headerTitle}>Send Notification</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      <ScrollView 
        style={styles.content} 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Notification Details */}
        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Notification Details</ThemedText>

          <View style={styles.inputContainer}>
            <ThemedText style={[styles.label, { color: theme.textSecondary }]}>
              Title
            </ThemedText>
            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  borderColor: theme.border,
                },
              ]}
              value={title}
              onChangeText={setTitle}
              placeholder="Enter notification title"
              placeholderTextColor={theme.textSecondary}
              maxLength={50}
            />
            <ThemedText style={[styles.charCount, { color: theme.textSecondary }]}>
              {title.length}/50
            </ThemedText>
          </View>

          <View style={styles.inputContainer}>
            <ThemedText style={[styles.label, { color: theme.textSecondary }]}>
              Message
            </ThemedText>
            <TextInput
              style={[
                styles.input,
                styles.messageInput,
                {
                  backgroundColor: theme.backgroundSecondary,
                  color: theme.text,
                  borderColor: theme.border,
                },
              ]}
              value={message}
              onChangeText={setMessage}
              placeholder="Enter notification message"
              placeholderTextColor={theme.textSecondary}
              multiline
              maxLength={200}
              textAlignVertical="top"
            />
            <ThemedText style={[styles.charCount, { color: theme.textSecondary }]}>
              {message.length}/200
            </ThemedText>
          </View>
        </View>

        {/* Recipients Info */}
        <View style={styles.section}>
          <View style={[styles.infoBox, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}>
            <Ionicons name="people" size={24} color={theme.accent} />
            <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
              This notification will be sent to all users
            </ThemedText>
          </View>
        </View>
      </ScrollView>

      {/* Send Button - Sticky at bottom */}
      <SafeAreaView style={[styles.buttonContainer, { backgroundColor: theme.background }]} edges={['bottom']}>
        <TouchableOpacity
          style={[
            styles.sendButton,
            {
              backgroundColor: theme.accent,
              opacity: isSending ? 0.6 : 1,
            },
          ]}
          onPress={handleSendNotification}
          disabled={isSending}
        >
          {isSending ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Ionicons name="send" size={20} color="#fff" />
              <ThemedText style={styles.sendButtonText}>Send Notification</ThemedText>
            </>
          )}
        </TouchableOpacity>
      </SafeAreaView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  messageInput: {
    minHeight: 100,
    paddingTop: 12,
  },
  charCount: {
    fontSize: 12,
    textAlign: 'right',
    marginTop: 4,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  buttonContainer: {
    padding: 16,
  },
  sendButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
  },
  sendButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
});

export default AdminNotificationScreen;
